import type { FC, PropsWithChildren } from 'react';
import { createContext, useState } from 'react';
import type { StoreApi } from 'zustand';

import { Redirect, useNavigate } from '@xing-com/crate-core-router';
import { PageError } from '@xing-com/crate-entity-pages-entity-pages-shared-components-page-error';

import { useEntityPage } from '../../hooks/use-entity-page/use-entity-page';
import { useIsWebview } from '../../hooks/use-is-webview/use-is-webview';
import { createEntityPageStore } from '../../store/entity-page-store/entity-page-store';
import type { EntityPageData } from '../../types/entity-page';
import type { EntityPageStore } from '../../types/entity-page-store';
import type { HeaderModuleData } from '../../types/header-module';

export const PageContext = createContext<StoreApi<EntityPageStore> | undefined>(
  undefined
);

type PageContextProviderInnerProps = {
  entityPage: EntityPageData;
  headerModule?: HeaderModuleData;
  isSubpage?: boolean;
};
const PageContextProviderInner: React.FC<
  React.PropsWithChildren<PageContextProviderInnerProps>
> = ({ entityPage, headerModule, isSubpage, children }) => {
  const isWebview = useIsWebview();
  const navigate = useNavigate();

  const [store] = useState(() =>
    createEntityPageStore({
      entityPage,
      headerModule,
      navigate,
      isWebview,
      isSubpage,
    })
  );

  return <PageContext.Provider value={store}>{children}</PageContext.Provider>;
};

type PageContextProviderProps = {
  pageSlug: string;
  isSubpage?: boolean;
};
export const PageContextProvider: FC<
  PropsWithChildren<PageContextProviderProps>
> = ({ pageSlug, isSubpage = false, children }) => {
  const { entityPage, headerModule, errorCode, redirectTo } = useEntityPage({
    pageSlug,
  });

  if (redirectTo) return <Redirect to={redirectTo} />;
  if (errorCode || !entityPage) return <PageError type={errorCode} />;

  return (
    <PageContextProviderInner
      entityPage={entityPage}
      headerModule={headerModule}
      isSubpage={isSubpage}
    >
      {children}
    </PageContextProviderInner>
  );
};
